import React, { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';

const PlayerMetricsRadar = ({ profile, isPitcher }) => {
  const careerStats = isPitcher ? profile?.pitching : profile?.hitting;

  const radarOption = useMemo(() => {
    if (!careerStats) return null;

    if (!isPitcher) {
      const avg = parseFloat(careerStats.avg) || 0;
      const obp = parseFloat(careerStats.obp) || 0;
      const slg = parseFloat(careerStats.slg) || 0;
      const ops = parseFloat(careerStats.ops) || 0;
      const hr = Math.min(parseFloat(careerStats.homeRuns) || 0, 500);
      const rbi = Math.min(parseFloat(careerStats.rbi) || 0, 1500);

      return {
        backgroundColor: 'transparent',
        radar: {
          indicator: [
            { name: 'AVG', max: 0.350 },
            { name: 'OBP', max: 0.450 },
            { name: 'SLG', max: 0.650 },
            { name: 'OPS', max: 1.100 },
            { name: 'POWER (HR)', max: 500 },
            { name: 'CLUTCH (RBI)', max: 1500 },
          ],
          shape: 'polygon',
          axisName: { color: '#a9c9b1', fontSize: 10, fontWeight: 'bold' },
          splitLine: { lineStyle: { color: 'rgba(101,141,113,0.3)' } },
          splitArea: { areaStyle: { color: ['rgba(0,0,0,0.05)', 'rgba(0,0,0,0.1)'] } },
          axisLine: { lineStyle: { color: 'rgba(101,141,113,0.4)' } },
        },
        series: [{
          type: 'radar',
          data: [{
            value: [avg, obp, slg, ops, hr, rbi],
            name: 'Career Performance',
            lineStyle: { color: '#d8ceb3', width: 3 },
            areaStyle: { color: 'rgba(216,206,179,0.2)' },
            itemStyle: { color: '#d8ceb3' },
            symbol: 'circle',
            symbolSize: 6
          }],
        }],
      };
    } else {
      const eraScore = Math.max(0, 10 - (parseFloat(careerStats.era) || 5));
      const whipScore = Math.max(0, 2.5 - (parseFloat(careerStats.whip) || 1.5));
      const k = Math.min(parseFloat(careerStats.strikeOuts) || 0, 3000);
      const w = Math.min(parseFloat(careerStats.wins) || 0, 300);
      const ip = Math.min(parseFloat(careerStats.inningsPitched) || 0, 3000);
      const k9 = Math.min(parseFloat(careerStats.strikeoutsPer9Inn) || 0, 12);

      return {
        backgroundColor: 'transparent',
        radar: {
          indicator: [
            { name: 'ERA Efficiency', max: 10 },
            { name: 'WHIP Control', max: 2.5 },
            { name: 'Total K', max: 3000 },
            { name: 'Wins', max: 300 },
            { name: 'Volume (IP)', max: 3000 },
            { name: 'K/9 Dominance', max: 12 },
          ],
          shape: 'polygon',
          axisName: { color: '#a9c9b1', fontSize: 10, fontWeight: 'bold' },
          splitLine: { lineStyle: { color: 'rgba(101,141,113,0.3)' } },
          splitArea: { areaStyle: { color: ['rgba(0,0,0,0.05)', 'rgba(0,0,0,0.1)'] } },
        },
        series: [{
          type: 'radar',
          data: [{
            value: [eraScore, whipScore, k, w, ip, k9],
            name: 'Career Performance',
            lineStyle: { color: '#e85555', width: 3 },
            areaStyle: { color: 'rgba(232,85,85,0.2)' },
            itemStyle: { color: '#e85555' },
            symbol: 'circle',
            symbolSize: 6
          }],
        }],
      };
    }
  }, [careerStats, isPitcher]);

  const StatBox = ({ label, value, accent }) => (
    <div className="flex flex-col items-center border border-[#658d71] p-3 bg-[rgba(0,0,0,0.12)]">
      <span className="text-[8px] uppercase tracking-widest text-[#658d71] font-black">{label}</span>
      <span className={`text-base font-black tabular-nums ${accent ? 'text-[#d8ceb3]' : 'text-white'}`}>
        {value ?? '--'}
      </span>
    </div>
  );

  if (!careerStats) return null;

  return (
    <div className="flex flex-col gap-4">
      <div className="border border-[#658d71] bg-[rgba(0,0,0,0.08)]">
        <div className="px-4 py-3 border-b border-[#658d71] bg-[rgba(0,0,0,0.1)] flex justify-between items-center">
          <span className="text-xs font-black uppercase tracking-widest text-[#d8ceb3]">Career Performance Radar</span>
          <span className="text-[9px] text-[#658d71] font-bold uppercase tracking-widest">Historical Normals</span>
        </div>
        <div className="p-4">
          {radarOption ? (
            <ReactECharts option={radarOption} style={{ height: '300px' }} />
          ) : (
            <div className="h-[300px] flex items-center justify-center text-[#658d71] text-xs uppercase tracking-widest">
              Processing Career Metrics...
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
        {!isPitcher ? (
          <>
            <StatBox label="AVG" value={careerStats.avg} accent />
            <StatBox label="OPS" value={careerStats.ops} accent />
            <StatBox label="HR" value={careerStats.homeRuns} />
            <StatBox label="RBI" value={careerStats.rbi} />
            <StatBox label="Hits" value={careerStats.hits} />
            <StatBox label="G" value={careerStats.gamesPlayed} />
          </>
        ) : (
          <>
            <StatBox label="ERA" value={careerStats.era} accent />
            <StatBox label="WHIP" value={careerStats.whip} accent />
            <StatBox label="W-L" value={`${careerStats.wins}-${careerStats.losses}`} />
            <StatBox label="K" value={careerStats.strikeOuts} />
            <StatBox label="IP" value={careerStats.inningsPitched} />
            <StatBox label="SV" value={careerStats.saves} />
          </>
        )}
      </div>
    </div>
  );
};

export default PlayerMetricsRadar;